import { useState } from "react";
import { SubTaskPrompt, SubTaskRole, SubTaskStudent } from "../../interfaces/Task";
import Select from "react-select";
import { useTranslation } from "react-i18next";

export interface SiteSubTaskStudentProps {
    student: SubTaskStudent;
    saveCallback: CallableFunction;
    cancelCallback: CallableFunction;
    roles: SubTaskRole[];
    prompts: SubTaskPrompt[]
}

export default function SiteSubTaskStudentEdit(props: SiteSubTaskStudentProps) {
    const { t } = useTranslation();
    
    // L'opció s'identifica per la POSICIÓ (índex), no per _id: els rols/prompts
    // acabats de crear al dissenyador encara no tenen _id (és '') fins que es
    // desa la tasca, i react-select necessita valors únics per distingir-los.
    const getRole = (position: number) => {
        const role = props.roles[position];
        if (role == undefined) {
            return {value: -1, label: t("- Select a role -")};
        } else {
            return {value: position, label: role.name};
        }
    }

    const getPrompt = (position: number) => {
        const prompt = props.prompts[position];
        if (prompt == undefined) {
            return {value: -1, label: t("- Select a prompt -")};
        } else {
            return {value: position, label: prompt.name};
        }
    }

    const [name, setName] = useState(props.student.name);
    const [role, setRole] = useState(props.student.role);
    const [roleValue, setRoleValue] = useState<any>(getRole(props.student.role));
    const [prompt, setPrompt] = useState<any>(props.student.prompt);
    const [promptValue, setPromptValue] = useState<any>(getPrompt(props.student.prompt));

    const roleOptions = [
        {value: -1, label: t("- Select a role -")},
        ...props.roles.map((r: SubTaskRole, index: number) => {
            return {
                value: index,
                label: r.name
            }
        })
    ];

    const promptOptions = [
        {value: -1, label: t("- Select a prompt -")},
        ...props.prompts.map((p: SubTaskPrompt, index: number) => {
            return {
                value: index,
                label: p.name
            }
        })
    ];

    // El menú es renderitza en un portal a <body> perquè el modal (amb overflow)
    // no el retalli quan la llista d'opcions és llarga. z-index per sobre de
    // l'overlay (.overlay és 1000).
    const menuPortalTarget = typeof document !== 'undefined' ? document.body : undefined;
    const selectStyles = { menuPortal: (base: any) => ({ ...base, zIndex: 100000 }) };

    const handleNameChange = (e: any) => {
        setName(e.target.value);
    }

    const save = () => {
        const newStudent = Object.assign({}, props.student);
        newStudent.name = name;
        newStudent.role = role;
        newStudent.prompt = prompt;
        props.saveCallback(newStudent);
    }

    const cancel = (e: any) => {
        props.cancelCallback();
    }

    const handleRoleChange = (e: any) => {
        const position = e.value;   // ja és l'índex (o -1 per "cap")
        setRole(position);
        if (position == -1) {
            setRoleValue(roleOptions[0]);
        } else {
            setRoleValue(roleOptions[position + 1]);
        }
    }

    const handlePromptChange = (e: any) => {
        const position = e.value;   // ja és l'índex (o -1 per "cap")
        setPrompt(position);
        if (position == -1) {
            setPromptValue(promptOptions[0]);
        } else {
            setPromptValue(promptOptions[position + 1]);
        }
    }

    return(
        <div className="site-sub-task-student-edit-container">
            <div className="edit-controls">
                <h4 className="text-truncate">{ 'Editing student' } <span>{ name }</span></h4>
                <input type="text" defaultValue={ props.student.name } onChange={ handleNameChange } />
                <Select className="role" options={ roleOptions } onChange={ handleRoleChange } value={ roleValue }
                    menuPortalTarget={ menuPortalTarget } styles={ selectStyles } />
                <Select className="prompt" options={ promptOptions } onChange={ handlePromptChange } value={ promptValue }
                    menuPortalTarget={ menuPortalTarget } styles={ selectStyles } />
                <div className="edit-actions">
                    <button className="btn btn-primary" title={ t("Save") } onClick={ save }>{ t("Save") }</button>
                    <button className="btn btn-secondary" title={ t("Cancel") } onClick={ cancel }>{ t("Cancel") }</button>
                </div>
            </div>
        </div>
    );
}